import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../api";
import { Logo, AironixBadge } from "../components/Logo";
import { LogIn, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name.split(" ")[0]}!`);
      navigate(u.role === "super_admin" ? "/admin" : "/app/dashboard");
    } catch (err) {
      toast.error(apiError(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to continue your cybersecurity journey.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="login-email" />
        </div>
        <div>
          <label className="label">Password</label>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" data-testid="login-password" />
        </div>
        <button className="btn-primary w-full !py-3" disabled={busy} data-testid="login-submit">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />} Log in
        </button>
      </form>
      <p className="text-center text-sm text-slate-400 mt-6">
        New here? <Link to="/register" className="text-[#F26522] font-medium hover:underline" data-testid="to-register">Create an account</Link>
      </p>
      {/* <div className="mt-6 p-3 rounded-lg bg-[#0B1120] border border-white/5 text-xs text-slate-400">
        <p className="font-medium text-slate-300 mb-1">Demo accounts</p>
        <p>Admin: admin@infosecgig.com / Admin@123</p>
        <p>Learner: student@infosecgig.com / Student@123</p>
      </div> */}
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0B1120] flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative p-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0" style={{ backgroundImage: "url('https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&w=940')", backgroundSize: "cover", opacity: 0.15 }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1120] via-transparent to-[#0B1120]" />
        <div className="relative"><Link to="/"><Logo className="h-10" withText /></Link></div>
        <div className="relative">
          <h2 className="font-heading text-4xl font-bold leading-tight">Learn. Practice.<br />Protect. <span className="text-[#F26522]">Excel.</span></h2>
          <p className="text-slate-400 mt-4 max-w-md">Master cybersecurity through structured paths, real assessments and safe hacking challenges.</p>
        </div>
        <div className="relative"><AironixBadge /></div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fadeup">
          <div className="lg:hidden mb-8"><Link to="/"><Logo className="h-9" withText /></Link></div>
          <h1 className="font-heading text-3xl font-bold">{title}</h1>
          <p className="text-slate-400 mt-2 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
